/**
 * Create a PulseAssist Email account.
 *
 * ═══ Why this screen has to exist ════════════════════════════════════════════
 *
 * PulseAssist Email is sold on its own. Before this, the only way to get an account was to register on
 * the AI platform first — so a customer who wanted email infrastructure and nothing else had to acquire
 * an AI workspace, an AI plan and a pot of AI trial tokens they never asked for, then come here and
 * sign in. The console's sign-in screen even said so out loud.
 *
 * Everything registered here sends `product: "email"`, which makes the server provision an Email
 * product subscription and NO AI subscription. That is not cosmetic: the two products bill through
 * entirely separate tables, and holding one has never entitled you to the other.
 *
 * ═══ Two routes in, and Google is the short one ═══════════════════════════════
 *
 * The password route is genuinely three steps, because registration REFUSES (403
 * `email_not_verified`) unless a mailed code has been confirmed first — the server will not create an
 * account on an address nobody has proven they can read. So: details → code → done.
 *
 * Google collapses that to one click, and legitimately rather than by skipping anything. Google asserts
 * `email_verified` and the server refuses the token outright when it is false, so mailbox control is
 * already proven by a signature over an audience naming this application. That is a stronger proof than
 * a six-digit code we mailed, not a weaker one — which is why the Google path needs no OTP step and is
 * offered first.
 *
 * ═══ What this screen does NOT do ════════════════════════════════════════════
 *
 * It does not ask for a plan or a card. A new account lands on the `email_free` tier, which is a real
 * tier in `email_product_plans` with real limits, so the console works immediately and upgrading is a
 * separate, later decision. Asking for payment details before someone has sent a single test message
 * would be asking them to buy something they have not seen.
 */
import { useEffect, useRef, useState } from "react";
import { Icon } from "../components/Icon";
import { GoogleButton } from "../components/GoogleButton";
import {
  fetchCaptchaConfig, fetchSsoConfig, signInWithGoogle,
  sendSignUpCode, confirmSignUpCode, registerEmailAccount,
  SignInError, type CaptchaConfig, type SsoConfig,
} from "../lib/session";
import { LEGAL } from "../lib/legal-links";

const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  const existing = document.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SRC}"]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("turnstile failed to load")));
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = TURNSTILE_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("turnstile failed to load"));
    document.head.appendChild(script);
  });
}

const FIELD_STYLE = {
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  background: "var(--surface)",
  fontSize: "var(--text-dense)",
  color: "var(--text)",
} as const;

/** The server's minimum. Stated on screen so it is not discovered by being refused. */
const PASSWORD_MIN = 8;

export function SignUpPage({
  onSignedUp,
  onSignIn,
}: {
  onSignedUp: () => void;
  onSignIn: () => void;
}) {
  const [step, setStep] = useState<"details" | "code">("details");

  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState("");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [sso, setSso] = useState<SsoConfig | null>(null);
  const [captcha, setCaptcha] = useState<CaptchaConfig | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaBroken, setCaptchaBroken] = useState(false);
  const widgetHost = useRef<HTMLDivElement | null>(null);
  const widgetId = useRef<string | null>(null);

  useEffect(() => {
    void fetchCaptchaConfig().then(setCaptcha);
    void fetchSsoConfig().then(setSso);
  }, []);

  // The widget lives on the CODE step, because that is the step that calls /auth/register — the
  // request the server wants a captcha token for.
  useEffect(() => {
    if (step !== "code") return;
    if (!captcha?.enabled || !captcha.siteKey || !widgetHost.current || widgetId.current) return;
    let cancelled = false;
    void loadTurnstile()
      .then(() => {
        if (cancelled || !widgetHost.current || !window.turnstile) return;
        widgetId.current = window.turnstile.render(widgetHost.current, {
          sitekey: captcha.siteKey,
          callback: (t: string) => setCaptchaToken(t),
          "expired-callback": () => setCaptchaToken(null),
          "error-callback": () => setCaptchaBroken(true),
          theme: document.documentElement.dataset.theme === "dark" ? "dark" : "light",
        });
      })
      .catch(() => setCaptchaBroken(true));
    return () => { cancelled = true; };
  }, [step, captcha]);

  const submitGoogle = async (credential: string) => {
    setBusy(true);
    setError(null);
    try {
      await signInWithGoogle(credential, "email");
      onSignedUp();
    } catch (err) {
      setError(
        err instanceof SignInError
          ? err.message
          : "Could not reach PulseAssist. Check your connection and try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  const requestCode = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await sendSignUpCode(email.trim().toLowerCase());
      setStep("code");
      setNotice(`We sent a 6-digit code to ${email.trim().toLowerCase()}.`);
    } catch (err) {
      /*
       * A 409 is not a failure worth a red box — it means they already have an account, and the useful
       * response is to point at sign-in rather than to report an error.
       */
      if (err instanceof SignInError && err.status === 409) {
        setError(`${err.message} Use "Sign in instead" below.`);
      } else {
        setError(err instanceof SignInError ? err.message : "Could not send a verification code.");
      }
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    setBusy(true);
    setError(null);
    try {
      // Two calls, in this order, because the server holds the proof of the OTP separately and
      // /auth/register consumes it. Confirming and registering in one request is not on offer.
      await confirmSignUpCode(email.trim().toLowerCase(), code.trim());
      await registerEmailAccount({
        email: email.trim().toLowerCase(),
        password,
        name: name.trim(),
        companyName: companyName.trim() || undefined,
        captchaToken,
      });
      onSignedUp();
    } catch (err) {
      setError(err instanceof SignInError ? err.message : "Could not create your account.");
      // The Turnstile token is single-use and is now spent whichever call failed.
      if (captcha?.enabled) {
        setCaptchaToken(null);
        try { window.turnstile?.reset(widgetId.current ?? undefined); } catch { /* re-rendered on remount */ }
      }
    } finally {
      setBusy(false);
    }
  };

  const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const detailsReady =
    name.trim().length > 0 && emailLooksValid && password.length >= PASSWORD_MIN && !busy;
  const captchaOutstanding = Boolean(captcha?.enabled) && !captchaBroken && !captchaToken;
  const codeReady = code.trim().length >= 4 && !busy && !captchaOutstanding;

  return (
    <div className="grid min-h-screen place-items-center px-4" style={{ background: "var(--canvas)" }}>
      <main className="w-full py-10" style={{ maxWidth: 380 }}>
        <div className="mb-7 flex items-center gap-[10px]">
          <span
            aria-hidden="true"
            className="block h-[26px] w-[26px] shrink-0 rounded-[6px]"
            style={{ background: "linear-gradient(135deg,#2563EB,#7C3AED)" }}
          />
          <span style={{ fontSize: "var(--text-section)" }}>
            <b style={{ fontWeight: 600 }}>PulseAssist</b>{" "}
            <span style={{ color: "var(--accent)", fontWeight: 500 }}>Email</span>
          </span>
        </div>

        <h1 style={{ fontSize: "var(--text-page)", fontWeight: 600, letterSpacing: "-0.01em" }}>
          {step === "details" ? "Create your account" : "Confirm your email"}
        </h1>

        {step === "details" ? (
          <>
            <p className="mt-1 mb-5" style={{ fontSize: "var(--text-dense)", color: "var(--muted)" }}>
              Email infrastructure, on its own. No PulseAssist AI subscription needed.
            </p>

            {sso?.google.enabled && sso.google.clientId && (
              <div className="mb-4 flex flex-col gap-3">
                <GoogleButton
                  clientId={sso.google.clientId}
                  text="signup_with"
                  disabled={busy}
                  onCredential={(c) => void submitGoogle(c)}
                />
                <div className="flex items-center gap-3" aria-hidden="true">
                  <span className="h-px flex-1" style={{ background: "var(--border)" }} />
                  <span className="micro" style={{ color: "var(--muted)" }}>or</span>
                  <span className="h-px flex-1" style={{ background: "var(--border)" }} />
                </div>
              </div>
            )}

            <form
              onSubmit={(e) => { e.preventDefault(); if (detailsReady) void requestCode(); }}
              className="flex flex-col gap-3"
            >
              <label className="flex flex-col gap-1.5">
                <span className="micro">Your name</span>
                <input
                  autoComplete="name"
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-[34px] px-[10px]"
                  style={FIELD_STYLE}
                />
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="micro">Company <span style={{ color: "var(--muted)" }}>(optional)</span></span>
                <input
                  autoComplete="organization"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="h-[34px] px-[10px]"
                  style={FIELD_STYLE}
                />
                <span style={{ fontSize: "var(--text-meta)", color: "var(--muted)" }}>
                  Names your workspace. You can change it later.
                </span>
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="micro">Work email</span>
                <input
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-[34px] px-[10px]"
                  style={FIELD_STYLE}
                />
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="micro">Password</span>
                <span className="relative flex items-center">
                  <input
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-[34px] w-full px-[10px] pr-9"
                    style={FIELD_STYLE}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute right-2 flex items-center"
                    style={{ color: "var(--muted)" }}
                  >
                    <Icon name={showPassword ? "eye-off" : "eye"} size={14} />
                  </button>
                </span>
                {/* Stated up front rather than discovered by being refused. */}
                <span style={{ fontSize: "var(--text-meta)", color: "var(--muted)" }}>
                  At least {PASSWORD_MIN} characters.
                </span>
              </label>

              {error && <ErrorNote>{error}</ErrorNote>}

              <button type="submit" className="pa-btn pa-btn-primary mt-1" style={{ height: 34 }} disabled={!detailsReady}>
                {busy ? "Sending code…" : "Continue"}
              </button>
              <p style={{ fontSize: "var(--text-meta)", color: "var(--muted)" }}>
                We will email you a 6-digit code to confirm the address.
              </p>
            </form>
          </>
        ) : (
          <>
            <p className="mt-1 mb-5" style={{ fontSize: "var(--text-dense)", color: "var(--muted)" }}>
              {notice ?? `Enter the code we sent to ${email.trim().toLowerCase()}.`}
            </p>

            <form
              onSubmit={(e) => { e.preventDefault(); if (codeReady) void finish(); }}
              className="flex flex-col gap-3"
            >
              <label className="flex flex-col gap-1.5">
                <span className="micro">Verification code</span>
                <input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  maxLength={8}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="mono h-[38px] px-[10px]"
                  style={{ ...FIELD_STYLE, fontSize: "var(--text-section)", letterSpacing: "0.18em" }}
                />
              </label>

              {captcha?.enabled && !captchaBroken && (
                <div ref={widgetHost} style={{ minHeight: 65 }} aria-label="Verification" />
              )}

              {captchaBroken && (
                <p
                  className="flex items-start gap-2 px-3 py-2"
                  style={{
                    background: "var(--warn-bg)",
                    border: "1px solid var(--warn-border)",
                    borderRadius: "var(--radius)",
                    fontSize: "var(--text-meta)",
                  }}
                >
                  <Icon name="warning" size={13} style={{ marginTop: 1, color: "var(--st-deferred)" }} />
                  <span>
                    The verification widget could not load. You can still continue — if verification is
                    required, the attempt will say so.
                  </span>
                </p>
              )}

              {error && <ErrorNote>{error}</ErrorNote>}

              <button type="submit" className="pa-btn pa-btn-primary mt-1" style={{ height: 34 }} disabled={!codeReady}>
                {busy ? "Creating your account…" : "Create account"}
              </button>

              {/*
                * Consent, at the point the account is actually created.
                *
                * There was no reference to the Terms or Privacy Policy anywhere in either sign-up flow
                * before this — the documents existed and nothing in the product pointed at them. This is
                * the one screen where a customer forms the agreement, so it is the one place the notice
                * has to be, and it sits with the button rather than at the top of the form where it
                * would be read before the reader knows what they are agreeing to.
                *
                * Not a checkbox. A required tick-box is a legitimate pattern, but adding one changes
                * whether an account can be created and how consent is recorded — a decision for the
                * owner and counsel, tracked in docs/legal/EMAIL_LEGAL_REVIEW.md, not for a UI change.
                * Plain anchors because the marketing site is a different origin.
                */}
              <p style={{ fontSize: "var(--text-meta)", color: "var(--muted)", lineHeight: 1.55 }}>
                By creating an account you agree to the{" "}
                <a href={LEGAL.emailTerms} target="_blank" rel="noreferrer" style={{ color: "var(--accent)" }}>
                  PulseAssist Email Terms of Service
                </a>{" "}
                and the{" "}
                <a href={LEGAL.emailPrivacy} target="_blank" rel="noreferrer" style={{ color: "var(--accent)" }}>
                  Privacy Policy
                </a>
                .
              </p>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => { setStep("details"); setError(null); setCode(""); }}
                  style={{ fontSize: "var(--text-meta)", color: "var(--muted)" }}
                >
                  ← Change details
                </button>
                <button
                  type="button"
                  onClick={() => void requestCode()}
                  disabled={busy}
                  style={{ fontSize: "var(--text-meta)", color: "var(--accent)" }}
                >
                  Resend code
                </button>
              </div>
            </form>
          </>
        )}

        <p className="mt-6" style={{ fontSize: "var(--text-dense)", color: "var(--muted)" }}>
          Already have an account?{" "}
          <button type="button" onClick={onSignIn} style={{ color: "var(--accent)", fontWeight: 500 }}>
            Sign in instead
          </button>
        </p>
      </main>
    </div>
  );
}

function ErrorNote({ children }: { children: React.ReactNode }) {
  return (
    <p
      role="alert"
      className="flex items-start gap-2 px-3 py-2"
      style={{
        background: "var(--warn-bg)",
        border: "1px solid var(--st-bounced)",
        borderRadius: "var(--radius)",
        fontSize: "var(--text-meta)",
      }}
    >
      <Icon name="critical" size={13} style={{ marginTop: 1, color: "var(--st-bounced)" }} />
      <span>{children}</span>
    </p>
  );
}
